import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowRight } from 'lucide-react';

export default function OljetTre() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative h-[600px] mb-0 overflow-hidden"
      >
        <img
          src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/47c712e54_generated_image.png"
          alt="Oljet Tre"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 to-black/20"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-lg tracking-widest-2xl font-body uppercase text-white/60 mb-6">Artikkel</p>
            <h1 className="font-heading text-6xl md:text-7xl lg:text-8xl font-light leading-[1.0] text-white mb-6">
              Oljet Tre
            </h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Tre behandlet med naturlige oljer som bevarer den naturlige fargen. Krever jevnlig vedlikehold.
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12">

        {/* MAIN CONTENT */}
        <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="py-20">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16">
              <h2 className="font-heading text-5xl font-light leading-[1.1] mb-8 text-[#2a1810]">
                Naturlig farve,<br />jevnlig vedlikehold
              </h2>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed mb-6">
                Oljet tre er for deg som ønsker å bevare treets naturlige utseende. Behandlingen med naturlige oljer gjør at materialet bevarerer sin varme og naturlige fargespill. Det krever regelmessig vedlikehold, men belønningen er en autentisk og levende fasade som eldes vakkert.
              </p>
              <p className="font-body text-2xl text-foreground/70 leading-relaxed">
                Oljet tre handler om å leve sammen med materialet og oppfølge det over tid. Det er ikke en set-and-forget løsning, men en aktiv relasjon til ditt bygg.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="rounded-[8px] overflow-hidden mb-16">
              <img src="https://media.base44.com/images/public/69ddf0e05a57ca57b48414c4/47c712e54_generated_image.png" alt="Oljet tre fasade" className="w-full h-auto object-cover" />
            </motion.div>

            {/* FORDELER */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16">
              <h3 className="font-heading text-3xl font-light mb-8 text-[#2a1810]">Fordeler med oljet tre</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { title: 'Naturlig utseende', desc: 'Bevarer treets naturlige fargespill og varme tone over tid. Materialet \"lever\" og endres subtilt med årstidene.' },
                  { title: 'Vedlikeholdsvennlig', desc: 'Oljebehandling er lettere å opprettholdholde enn maling. En enkel slipning og re-oliering gir like nytt resultat.' },
                  { title: 'Fleksibel vedlikehold', desc: 'Du kan vedlikeholde små områder når behov oppstår, uten å måtte gjøre hele fasaden på samme tid.' },
                  { title: 'Autentisk materialkvalitet', desc: 'Treet "puster" og tar opp fuktighet naturlig. Det skaper mindre spenning i materialet og færre sprekker.' }
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    whileHover={{ y: -4 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: idx * 0.05 }}
                    className="bg-white border border-border/20 rounded-[8px] p-8 hover:border-[#2d5a3d] hover:shadow-md transition-all"
                  >
                    <h4 className="font-heading text-lg font-light text-[#2a1810] mb-3">{item.title}</h4>
                    <p className="font-body text-2xl text-foreground/60">{item.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* VEDLIKEHOLD */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16 bg-gradient-to-br from-[#2d5a3d]/10 to-transparent border-l-4 border-[#2d5a3d] rounded-[8px] p-8">
              <h3 className="font-heading text-2xl font-light mb-6 text-[#2a1810]">Vedlikehold av oljet tre</h3>
              <div className="space-y-4 font-body text-2xl text-foreground/70 leading-relaxed">
                <p>
                  <span className="font-semibold text-[#2a1810]">Årlig kontroll:</span> Sjekk fasaden hver vår eller høst for tegn på tørr eller slitt overflate.
                </p>
                <p>
                  <span className="font-semibold text-[#2a1810]">Slipning og oliering:</span> Hver 2-3 år, eller når overflaten begynner å bli tørr, bør du sleipe og re-oljere. Det er en relativ enkel prosess og krever ikke fagfolk.
                </p>
                <p>
                  <span className="font-semibold text-[#2a1810]">Valg av olje:</span> Bruk kvalitetsoljer som er laget for nordisk klima og UV-eksponering. Vi anbefaler miljøvennlige og bio-baserte alternativer.
                </p>
                <p>
                  <span className="font-semibold text-[#2a1810]">Lokal vedlikehold:</span> En av fordelene med oljet tre er at du kan vedlikeholde mindre områder etter behov, uten hele fasaden må gjøres samtidig.
                </p>
              </div>
            </motion.div>

            {/* SAMMENLIKNING */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="mb-16">
              <h3 className="font-heading text-2xl font-light mb-8 text-[#2a1810]">Oljet tre vs. andre løsninger</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-2xl">
                  <thead>
                    <tr className="border-b-2 border-[#2d5a3d]">
                      <th className="text-left py-4 px-4 font-heading font-light text-[#2a1810]">Egenskap</th>
                      <th className="text-left py-4 px-4 font-heading font-light text-[#2a1810]">Oljet Tre</th>
                      <th className="text-left py-4 px-4 font-heading font-light text-[#2a1810]">Kebony</th>
                      <th className="text-left py-4 px-4 font-heading font-light text-[#2a1810]">Malt</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border/30">
                      <td className="py-4 px-4 font-body text-foreground">Vedlikehold</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Regelmessig (2-3 år)</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Minimal</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Regelmessig (5-7 år)</td>
                    </tr>
                    <tr className="border-b border-border/30">
                      <td className="py-4 px-4 font-body text-foreground">Naturlig utseende</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Høyt</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Gråning over tid</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Kontrollert</td>
                    </tr>
                    <tr className="border-b border-border/30">
                      <td className="py-4 px-4 font-body text-foreground">Pris</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Moderat</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Høyere</td>
                      <td className="py-4 px-4 font-body text-foreground/70">Moderat</td>
                    </tr>
                    <tr>
                      <td className="py-4 px-4 font-body text-foreground">Levetid</td>
                      <td className="py-4 px-4 font-body text-foreground/70">20-30 år</td>
                      <td className="py-4 px-4 font-body text-foreground/70">40+ år</td>
                      <td className="py-4 px-4 font-body text-foreground/70">20-30 år</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="bg-[#2a1810] text-white rounded-[8px] p-12 text-center">
              <h3 className="font-heading text-3xl font-light mb-4">Klart til å velge oljet tre?</h3>
              <p className="font-body text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
                Oljet tre er perfekt hvis du ønsker å leve aktivt sammen med materialet og belønnes med en naturlig, levende fasade over år.
              </p>
              <motion.a whileHover={{ scale: 1.02 }} href="/sauna-ly" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-2xl font-semibold rounded-[4px] hover:bg-white/90 transition-all">
                Konfigurier badstue <ArrowRight size={16} />
              </motion.a>
            </motion.div>

            <motion.a whileHover={{ scale: 1.02 }} href="/laer-mer-handverk"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all mt-16">
              Tilbake til Lær Mer <ArrowRight size={16} />
            </motion.a>
          </div>
        </motion.section>

      </div>

      <Footer />
    </div>
  );
}